import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import AppLink from './AppLink';
import { partyInfo } from '../mockData';
import { DriveBar } from './DriveBar';
import { CartButton } from './shop/CartButton';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  const navigation = [
    { name: { en: 'Home', bn: 'হোম' }, href: '/' },
    { name: { en: 'About', bn: 'সম্পর্কে' }, href: '/about' },
    { name: { en: 'Leadership', bn: 'নেতৃত্ব' }, href: '/leadership' },
    { name: { en: 'News', bn: 'সংবাদ' }, href: '/news' },
    { name: { en: 'Events', bn: 'ইভেন্ট' }, href: '/events' },
    { name: { en: 'Shop', bn: 'শপ' }, href: '/shop' },
    { name: { en: 'Party Symbols', bn: 'দলীয় প্রতীক' }, href: '/our-flag', also: ['/theme-song', '/our-tiger', '/our-roundel'] },
    { name: { en: 'Gallery', bn: 'গ্যালারি' }, href: '/gallery' },
    { name: { en: 'Districts', bn: 'জেলা' }, href: '/districts' },
    { name: { en: 'ID Card', bn: 'পরিচয়পত্র' }, href: '/member-card' },
    { name: { en: 'Contact', bn: 'যোগাযোগ' }, href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md" data-testid="main-header">
      {location.pathname === '/' && <DriveBar />}
      {/* Top bar with party colors */}
      <div className="flex h-1">
        <div className="flex-1 bg-party-red"></div>
        <div className="flex-1 bg-white"></div>
        <div className="flex-1 bg-party-green"></div>
      </div>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <AppLink to="/" className="flex items-center space-x-3" data-testid="header-logo">
            <div className="flex items-center">
              <img
                src={partyInfo.flagImage}
                alt="NDP Flag"
                className="h-12 w-12 rounded-lg object-contain shadow-sm"
              />
              <div className="ml-3">
                <div className="text-lg font-bold text-party-red leading-tight">NDP</div>
                <div className="text-xs text-gray-500 leading-tight">{t(partyInfo.shortName)}</div>
              </div>
            </div>
          </AppLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-4 xl:space-x-6">
            {navigation.map((item) => (
              <AppLink
                key={item.href}
                to={item.href}
                className={`whitespace-nowrap text-sm font-medium transition-colors hover:text-party-red ${
                  location.pathname === item.href || item.also?.includes(location.pathname) ? 'text-party-red font-bold' : 'text-gray-700'
                }`}
                data-testid={`nav-${item.href.replace('/', '') || 'home'}`}
              >
                {t(item.name)}
              </AppLink>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden lg:flex lg:items-center lg:space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-gray-600 hover:text-party-red"
              data-testid="language-toggle-btn"
            >
              <Globe className="h-4 w-4 mr-1" />
              {language === 'en' ? 'বাংলা' : 'English'}
            </Button>
            <AppLink to="/join">
              <Button size="sm" className="bg-party-green hover:bg-party-green-600 text-white" data-testid="header-join-btn">
                {t({ en: 'Join Us', bn: 'যোগ দিন' })}
              </Button>
            </AppLink>
            <AppLink to="/donate">
              <Button size="sm" variant="outline" className="border-party-red text-party-red hover:bg-party-red hover:text-white" data-testid="header-donate-btn">
                {t({ en: 'Donate', bn: 'দান করুন' })}
              </Button>
            </AppLink>
            <CartButton testid="header-cart-btn" />
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-1 lg:hidden">
            <CartButton testid="mobile-cart-btn" />
            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navigation.map((item) => (
              <AppLink
                key={item.href}
                to={item.href}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === item.href || item.also?.includes(location.pathname) ? 'bg-party-red-50 text-party-red' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(item.name)}
              </AppLink>
            ))}
            <div className="pt-4 border-t space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="w-full justify-start"
              >
                <Globe className="h-4 w-4 mr-2" />
                {language === 'en' ? 'বাংলা' : 'English'}
              </Button>
              <AppLink to="/join" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" className="w-full bg-party-green hover:bg-party-green-600 text-white">
                  {t({ en: 'Join Us', bn: 'যোগ দিন' })}
                </Button>
              </AppLink>
              <AppLink to="/donate" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" className="w-full bg-party-red hover:bg-party-red-600 text-white">
                  {t({ en: 'Donate', bn: 'দান করুন' })}
                </Button>
              </AppLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
